import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Crown } from 'lucide-react';
import { API } from '../App';

/**
 * Homepage rail showcasing the Christ Kingdom Apparel POD line.
 * Auto-hides if there are zero house-brand products.
 */
export default function HouseBrandRail() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHouseBrand = async () => {
      try {
        const r = await axios.get(`${API}/products?house_brand=true&limit=8`);
        setProducts(r.data.products || []);
      } catch (e) {
        // Non-blocking: hide rail if endpoint fails
      } finally {
        setLoading(false);
      }
    };
    fetchHouseBrand();
  }, []);

  if (loading) {
    return null;
  }
  if (!products.length) {
    return null;
  }

  return (
    <section
      className="py-16 md:py-20 bg-gradient-to-br from-amber-50 via-orange-50/40 to-amber-50"
      data-testid="house-brand-rail"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700 bg-amber-100 px-3 py-1.5 rounded-full mb-3">
              <Crown className="h-3.5 w-3.5" />
              Official Merch
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-foreground">
              Christ Kingdom Apparel
            </h2>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Wear the Kingdom. Carry the Calling. Premium designs, printed-on-demand and shipped globally.
            </p>
          </div>
          <Link
            to="/store/christ-kingdom-apparel"
            className="inline-flex items-center gap-1 text-primary hover:underline font-medium whitespace-nowrap"
            data-testid="house-brand-shop-all"
          >
            Shop the collection
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide">
          {products.map((p) => (
            <Link
              key={p.product_id}
              to={`/products/${p.product_id}`}
              className="group flex-shrink-0 w-[220px] sm:w-[240px] snap-start"
              data-testid={`house-brand-product-${p.product_id}`}
            >
              <div className="aspect-square rounded-2xl overflow-hidden bg-white border border-amber-200/60 shadow-sm group-hover:shadow-md transition-shadow">
                {p.images && p.images[0] ? (
                  <img
                    src={p.images[0]}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-amber-100 flex items-center justify-center">
                    <Crown className="h-12 w-12 text-amber-400" />
                  </div>
                )}
              </div>
              <div className="mt-3 px-1">
                <h3 className="font-medium text-foreground text-sm line-clamp-2 leading-snug">{p.name}</h3>
                <p className="text-primary font-semibold mt-1">${(p.price || 0).toFixed(2)}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
